import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CardComponent } from '@app/shared/common/card/card.component';
import { getGrowthStatementByCategory } from '@app/utils/growth-statement';
import { UserService } from '@app/services/api/user/user.service';
import { analytics } from '@app/events/segment/analytics';

const enum AreaType {
  NATIONAL = 'NATIONAL',
  STATE = 'STATE',
  CBSA = 'CBSA'
};

@Component({
  selector: 'app-salary-growth',
  imports: [
    CommonModule,
    CardComponent,
  ],
  templateUrl: './salary-growth.component.html',
  styleUrl: './salary-growth.component.css'
})
export class SalaryGrowthComponent implements OnInit {
  @Input() areaMedianSalary: {
    area: string | null;
    areaType: string | null;
    medianSalary: string | null;
  } = {
      area: null,
      areaType: null,
      medianSalary: null
    };
  @Input() areaGrowthProjection: {
    area: string | null;
    areaType: string | null;
    growthCategory: string | null;
    percentChange: number | null;
  } = {
      area: null,
      areaType: null,
      growthCategory: null,
      percentChange: null
    };
  public displayFutureLocation: string = '';

  constructor(private userService: UserService) { }

  ngOnInit() {
    this.loadUserLocation();
  }

  private loadUserLocation() {
    this.userService.getMe().subscribe({
      next: (profile) => {
        if (profile.backgroundInfo?.future_location) {
          const futureLocation = profile.backgroundInfo.future_location;

          // Parse location from future_location (format: "Los Angeles, TX, USA")
          const locationParts = futureLocation.split(',').map(part => part.trim());

          if (locationParts.length >= 2) {
            // Take city and state (exclude country)
            const cityAndState = locationParts.slice(0, -1).join(', ');
            this.displayFutureLocation = cityAndState; // "Los Angeles, TX"
          }
        }
      },
      error: (error) => {
        console.warn('Failed to load user location:', error);
      }
    });
  }

  public readonly salaryTooltip = `
    Half of the workers in a given occupation earn more than the median wage,
    and half the workers earn less than the median wage.
  `;
  public readonly growthTooltip = `
    Projected percent change in the number of jobs added or
    lost in the occupation or industry
  `;

  get growthStatement(): string {
    return getGrowthStatementByCategory(this.areaGrowthProjection.growthCategory);
  }

  get jobGrowthHeading(): string {
    if (this.areaGrowthProjection.areaType === AreaType.NATIONAL) {
      return 'Forecasted National Job Growth';
    } else if (this.areaGrowthProjection.areaType === AreaType.STATE && this.areaGrowthProjection.area) {
      return `Forecasted ${this.areaGrowthProjection.area} Job Growth`;
    } else {
      return 'Forecasted Job Growth';
    }
  }

  get jobGrowthPercentage(): string {
    const percentage = this.areaGrowthProjection.percentChange;
    if (percentage === null || percentage === undefined) {
      return 'N/A';
    }
    return percentage > 0 ? `+${percentage}%` : `${percentage}%`;
  }

  get medianSalaryHeading(): string {
    if (this.areaMedianSalary.areaType === AreaType.NATIONAL) {
      return 'National Median Salary';
    } else if (this.areaMedianSalary.areaType === AreaType.STATE && this.areaMedianSalary.area) {
      return `Median Salary in ${this.areaMedianSalary.area}`;
    } else if (this.areaMedianSalary.areaType === AreaType.CBSA && this.displayFutureLocation) {
      return `Median Salary in ${this.displayFutureLocation}`;
    } else {
      return 'Median Salary';
    }
  }

  get formattedMedianSalary(): string {
    const medianSalary = this.areaMedianSalary.medianSalary;

    if (medianSalary === null || medianSalary === undefined) {
      return 'N/A';
    }

    // Format with commas and add dollar sign
    return '$' + parseFloat(medianSalary).toLocaleString('en-US');
  }

  get growthBars(): boolean[] {
    const bars = [false, false, false, false, false];
    const filledBars = this.getFilledBarsCount();

    for (let i = 0; i < filledBars; i++) {
      bars[i] = true;
    }
    return bars;
  }

  private getFilledBarsCount(): number {
    switch (this.areaGrowthProjection.growthCategory) {
      case 'MUCH_FASTER':
        return 5;
      case 'FASTER':
        return 4;
      case 'AVERAGE':
        return 3;
      case 'SLOWER':
        return 2;
      case 'NO_CHANGE':
        return 1;
      case 'DECLINE':
        return 0;
      default:
        return 0;
    }
  }

  onSalaryInfoHover() {
    analytics.track('Salary Info Open', { section: 'explore' });
  }

  onGrowthInfoHover() {
    analytics.track('Growth Info Open', { section: 'explore' });
  }
}
